import React, { useEffect, useRef, useState } from 'react';
import Style from './ResumeRequestModal.module.scss';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUpRight, LockIcon } from '../ui/Icons';

const EMPTY = { name: '', email: '', company: '', purpose: '' };

export default function ResumeRequestModal({ open, onClose }) {
    const [fields, setFields] = useState(EMPTY);
    const [botField, setBotField] = useState('');
    const [status, setStatus] = useState('idle'); // idle | sending | done | error
    const [error, setError] = useState('');
    const [sentTo, setSentTo] = useState('');
    const firstRef = useRef(null);

    useEffect(() => {
        if (open) {
            setTimeout(() => firstRef.current?.focus(), 80);
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
            // reset shortly after close so the closing animation keeps its content
            const t = setTimeout(() => {
                setStatus('idle'); setFields(EMPTY); setError(''); setSentTo('');
            }, 300);
            return () => clearTimeout(t);
        }
        return () => { document.body.style.overflow = ''; };
    }, [open]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape' && open) onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    const set = (key) => (e) => setFields((f) => ({ ...f, [key]: e.target.value }));

    const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email);
    const canSend =
        fields.name.trim() && validEmail && fields.purpose.trim() && status !== 'sending';

    const submit = async (e) => {
        e.preventDefault();
        if (botField) return; // honeypot tripped — silently drop
        if (!canSend) return;
        setStatus('sending');
        setError('');
        try {
            const res = await fetch('/.netlify/functions/resume-request', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...fields, botField }),
            });
            if (!res.ok) {
                let msg = 'could not log your request — please try LinkedIn';
                try { const b = await res.json(); if (b.error) msg = b.error; } catch { /* keep default */ }
                throw new Error(msg);
            }
            setSentTo(fields.email.trim());
            setStatus('done');
        } catch (err) {
            setStatus('error');
            setError(err.message || 'something went wrong');
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    className={Style.backdrop}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.18 }}
                    onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
                >
                    <motion.div
                        className={Style.panel}
                        role="dialog"
                        aria-modal="true"
                        aria-label="request my resume"
                        initial={{ opacity: 0, y: 18, scale: 0.985 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 12, scale: 0.99 }}
                        transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <div className={Style.bar}>
                            <span className={Style.dot} />
                            sai@portfolio — resume request
                            <button className={Style.esc} onClick={onClose}>esc</button>
                        </div>

                        {status === 'done' ? (
                            <div className={Style.success}>
                                <p className={Style.ok}>✓ 202 Accepted</p>
                                <p className={Style.successMsg}>
                                    got your request — I'll send my resume to
                                    <br /><strong className={Style.sentTo}>{sentTo}</strong>
                                    <br />myself, usually within a day or two.
                                </p>
                                <button className={Style.sendBtn} onClick={onClose}>close</button>
                            </div>
                        ) : (
                            <form className={Style.form} onSubmit={submit} noValidate>
                                <p className={Style.request}>
                                    <span className={Style.method}>POST</span> /resume/request
                                    <span className={Style.hint}>
                                        <LockIcon size={11} /> not published
                                    </span>
                                </p>

                                <p className={Style.lede}>
                                    I don't post my resume publicly — I send it personally. Tell me
                                    where to send it and what it's for, and it'll land in your inbox.
                                </p>

                                {/* honeypot — hidden from humans */}
                                <input
                                    type="text"
                                    name="bot-field"
                                    tabIndex={-1}
                                    autoComplete="off"
                                    value={botField}
                                    onChange={(e) => setBotField(e.target.value)}
                                    className={Style.honeypot}
                                    aria-hidden="true"
                                />

                                <label className={Style.field}>
                                    <span>your.name</span>
                                    <input
                                        ref={firstRef}
                                        type="text"
                                        name="name"
                                        placeholder="your name"
                                        value={fields.name}
                                        onChange={set('name')}
                                        required
                                    />
                                </label>

                                <label className={Style.field}>
                                    <span>send.to</span>
                                    <input
                                        type="email"
                                        name="email"
                                        placeholder="you@company.com"
                                        value={fields.email}
                                        onChange={set('email')}
                                        required
                                    />
                                </label>

                                <label className={Style.field}>
                                    <span>company</span>
                                    <input
                                        type="text"
                                        name="company"
                                        placeholder="company or team"
                                        value={fields.company}
                                        onChange={set('company')}
                                    />
                                </label>

                                <label className={`${Style.field} ${Style.fieldArea}`}>
                                    <span>purpose</span>
                                    <textarea
                                        name="purpose"
                                        rows={3}
                                        placeholder="what's it for? — a role you're hiring for, a contract, a collaboration…"
                                        value={fields.purpose}
                                        onChange={set('purpose')}
                                        required
                                    />
                                </label>

                                {status === 'error' && (
                                    <p className={Style.errorLine}>✕ {error} — or reach me on LinkedIn instead</p>
                                )}

                                <div className={Style.actions}>
                                    <button type="submit" className={Style.sendBtn} disabled={!canSend}>
                                        {status === 'sending'
                                            ? '⟳ sending…'
                                            : <>request resume <ArrowUpRight size={13} /></>}
                                    </button>
                                    <span className={Style.note}>your details stay with me · never shared</span>
                                </div>
                            </form>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
